import type * as TabsPrimitive from "@radix-ui/react-tabs";
import type {
  AnchorHTMLAttributes,
  ComponentPropsWithoutRef,
  ComponentRef,
  HTMLAttributes,
  ReactNode,
} from "react";

import type { TIconKey } from "@/ui/components/primitives/Icon";
import { cn, type DefinedDefaults } from "@/ui/lib";

/*-------------------------------------
|  Constants
|-------------------------------------*/

export const TABS_VARIANT_CLASS_MAP = {
  default: {
    list: "flex justify-around",
    trigger: "text-foreground data-[state=active]:underline data-[state=active]:underline-offset-4 data-[state=active]:decoration-2 [&:not([data-state=active])]:focus-visible:text-foreground-muted",
    content: "px-1",
    label: "",
  },
  pill: {
    list: "rounded-sys-lg bg-component-tabs-list-variant-pill flex-grow p-1",
    trigger: "rounded-sys-md bg-component-tabs-trigger-variant-pill-default data-[state=active]:bg-component-tabs-trigger-variant-pill-active data-[state=active]:shadow-sm data-[state=active]:text-foreground-inverse flex-grow",
    content: "px-1",
    label: "",
  },
  card: {
    list: "flex space-x-1",
    trigger: cn(
      "w-28 py-1 px-2 text-foreground rounded-sys-xs border border-dashed border-sys-black bg-sys-neutral flex flex-col space-y-1",
      "data-[state=active]:bg-sys-black data-[state=active]:text-foreground-inverse",
    ),
    content: "",
    label: "",
  },
  icon: {
    list: "bg-white flex space-x-1 rounded-sys-md overflow-hidden",
    trigger: cn(
      "flex flex-col space-y-2 box-content",
      "w-20 bg-white py-1 px-2 text-foreground rounded-sys-md border border-solid border-white",
      "data-[state=active]:border-black/10 data-[state=active]:shadow-[0_0_6px_rgba(0,0,0,0.1)]",
    ),
    content: "",
    label: "truncate w-16",
    links: cn(
      "flex flex-col space-y-2 items-center box-content",
      "w-20 bg-sys-neutral-600 py-1 px-2 text-foreground rounded-sys-md border border-dashed border-black shadow-[0_0_6px_rgba(0,0,0,0.1)]",
    ),
  },
} as const;

/*-------------------------------------
|  Types
|-------------------------------------*/

export type TTabsVariant = keyof typeof TABS_VARIANT_CLASS_MAP;

type TTabsBaseItem = {
  content: ReactNode;
  description?: string;
  disabled?: boolean;
  label: ReactNode;
  value: string | number;
};

export type TTabsItem = TTabsBaseItem & {
  icon?: never;
};

export type TTabsWithIconItem = TTabsBaseItem & {
  icon: TIconKey;
};

export type TTabsLink = {
  href: string;
  icon: TIconKey;
  label: ReactNode;
  linkProps?: Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    "children" | "href"
  >;
};

export type TTabsLinksProps = {
  links?: TTabsLink[];
};

export type TTabLinkIconProps = {
  icon: TIconKey;
};

export type TTabsRootProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Root>;
export type TTabsListElement = ComponentRef<typeof TabsPrimitive.List>;
export type TTabsListProps = ComponentPropsWithoutRef<typeof TabsPrimitive.List> & {
  variant?: TTabsVariant;
};
export type TTabsTriggerElement = ComponentRef<typeof TabsPrimitive.Trigger>;
type TTabsTriggerBaseProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & {
  description?: string;
};

type TTabsTriggerWithoutIconProps = {
  icon?: never;
  variant?: Exclude<TTabsVariant, "icon">;
};

type TTabsTriggerWithIconProps = {
  icon: TIconKey;
  variant: "icon";
};

export type TTabsTriggerVariantProps =
  | TTabsTriggerWithoutIconProps
  | TTabsTriggerWithIconProps;

export type TTabsTriggerProps = TTabsTriggerBaseProps &
  TTabsTriggerVariantProps;

export type TTabsContentElement = ComponentRef<typeof TabsPrimitive.Content>;
export type TTabsContentProps = ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & {
  variant?: TTabsVariant;
};

type TTabsBaseProps = TTabsRootProps & {
  contentClassName?: string;
  hideTabList?: boolean;
  id: string;
  listClassName?: string;
  triggerClassName?: string;
} & Pick<HTMLAttributes<HTMLElement>, "className">;

type TTabsWithoutIconsProps = TTabsBaseProps & {
  links?: never;
  tabs: TTabsItem[];
  variant?: Exclude<TTabsVariant, "icon">;
};

type TTabsWithIconsProps = TTabsBaseProps & {
  links?: TTabsLink[];
  tabs: TTabsWithIconItem[];
  variant: "icon";
};

export type TTabsProps = TTabsWithoutIconsProps | TTabsWithIconsProps;

type TTabsTriggersBaseProps = Pick<TTabsBaseProps, "triggerClassName">;

type TTabsTriggersWithoutIconsProps = TTabsTriggersBaseProps & {
  tabs: TTabsItem[];
  variant?: Exclude<TTabsVariant, "icon">;
};

type TTabsTriggersWithIconsProps = TTabsTriggersBaseProps & {
  tabs: TTabsWithIconItem[];
  variant: "icon";
};

export type TTabsTriggersProps =
  | TTabsTriggersWithoutIconsProps
  | TTabsTriggersWithIconsProps;

type TTabsDefaultProps = DefinedDefaults<
  TTabsProps,
  "variant" | "hideTabList"
>;

/*-------------------------------------
|  Defaults
|-------------------------------------*/

export const TABS_DEFAULTS = {
  variant: "default",
  hideTabList: false,
} satisfies TTabsDefaultProps;
